import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { HistoryListKey, HistoryState, PaginationMetadata } from "../../types";
import { axiosObj } from "../../../config/apiClients";
import { formatTimeDiff, toUAETime } from "../../../utils/date";
import { queryParams } from "../../../utils/interface";
import { BarNumber, ClosePosition, PositionStats } from "../../../utils/types";

const HISTORY_KEYS: HistoryListKey[] = [
  "history", "open", "closed", "pendingBuy", "pendingSell",
];

const initialPagination: PaginationMetadata = {
  totalItems: 0,
  currentPage: 1,
  totalPages: 1,
  hasNextPage: false,
};

const buildInitial = <T>(value: () => T) =>
  Object.fromEntries(HISTORY_KEYS.map((k) => [k, value()])) as Record<HistoryListKey, T>;

const readPagination = (payload: any): PaginationMetadata => ({
  totalItems: payload?.totalItems ?? 0,
  currentPage: payload?.page ?? 1,
  totalPages: payload?.totalPages ?? 1,
  hasNextPage: payload?.hasNextPage ?? false,
});

const initialState: HistoryState = {
  listLoading: buildInitial(() => false),
  listPagination: buildInitial(() => ({ ...initialPagination })),
  isMutating: false,
  history: [],
  openPositions: [],
  closedPositions: [],
  pendingBuyPositions: [],
  pendingSellPositions: [],
  metalSummary: [],
  positionStats: null,
  positionStatsLoading: false,
  positionStatsError: null,
};

export const getHistory = createAsyncThunk(
  "/statement/getAll",
  async ({ paramPage, paramLimit, search }: queryParams, { rejectWithValue }) => {
    try {
      const response = await axiosObj.get(`statement/getAll?paramPage=${paramPage}&paramLimit=${paramLimit}${search ? `&search=${search}` : ""}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: 'Failed to Fetch History' });
    }
  }
);

export const getOpenedPositions = createAsyncThunk(
  "/trade/open",
  async ({ paramPage, paramLimit, search, startDate, endDate, tradeSymbol }: queryParams, { rejectWithValue }) => {
    try {
      const response = await axiosObj.get(`trade/open?paramPage=${paramPage}&paramLimit=${paramLimit}${search ? `&search=${search}` : ""}${startDate ? `&startDate=${startDate}` : ""}${endDate ? `&endDate=${endDate}` : ""}${tradeSymbol && tradeSymbol !== "all" ? `&tradeSymbol=${tradeSymbol}` : ""}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: 'Failed to Fetch Open Positions' });
    }
  }
);

export const getPendingBuyPositions = createAsyncThunk(
  "/trade/pendingBuyPosition",
  async ({ paramPage, paramLimit, search, startDate, endDate, tradeSymbol }: queryParams, { rejectWithValue }) => {
    try {
      const response = await axiosObj.get(`trade/pendingBuyPositions?paramPage=${paramPage}&paramLimit=${paramLimit}${search ? `&search=${search}` : ""}${startDate ? `&startDate=${startDate}` : ""}${endDate ? `&endDate=${endDate}` : ""}${tradeSymbol && tradeSymbol !== "all" ? `&tradeSymbol=${tradeSymbol}` : ""}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: 'Failed to Fetch Pending Buy Positions' });
    }
  }
);

export const getPendingSellPositions = createAsyncThunk(
  "/trade/pendingSellPositions",
  async ({ paramPage, paramLimit, search, startDate, endDate, tradeSymbol }: queryParams, { rejectWithValue }) => {
    try {
      const response = await axiosObj.get(`trade/pendingSellPositions?paramPage=${paramPage}&paramLimit=${paramLimit}${search ? `&search=${search}` : ""}${startDate ? `&startDate=${startDate}` : ""}${endDate ? `&endDate=${endDate}` : ""}${tradeSymbol && tradeSymbol !== "all" ? `&tradeSymbol=${tradeSymbol}` : ""}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: 'Failed to Fetch Pending Sell Positions' });
    }
  }
);

export const getClosedPositions = createAsyncThunk(
  "/trade/close",
  async ({ paramPage, paramLimit, search, startDate, endDate, tradeSymbol }: queryParams, { rejectWithValue }) => {
    try {
      const response = await axiosObj.get(`trade/close?paramPage=${paramPage}&paramLimit=${paramLimit}${search ? `&search=${search}` : ""}${startDate ? `&startDate=${startDate}` : ""}${endDate ? `&endDate=${endDate}` : ""}${tradeSymbol && tradeSymbol !== "all" ? `&tradeSymbol=${tradeSymbol}` : ""}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: 'Failed to Fetch Open Positions' });
    }
  }
);

// Optional customer search, metal (XAUUSD / XAGUSD) and plain days ("2026-09-01");
// without them the stats cover everyone, both metals, all time
export const getPositionStats = createAsyncThunk<
  PositionStats,
  { search?: string; tradeSymbol?: string; startDate?: string; endDate?: string } | undefined,
  { rejectValue: string }
>(
  "/trade/positionStats",
  async (dateRange, { rejectWithValue }) => {
    try {
      const response = await axiosObj.get('trade/positionStats', { params: dateRange });
      return response.data.payload;
    } catch (error) {
      return rejectWithValue((error as { message?: string })?.message || 'Failed to Fetch Position Stats');
    }
  }
);

export const manualClosePosition = createAsyncThunk(
  "/trade/manualClosePosition",
  async (closePosition: ClosePosition, { rejectWithValue }) => {
    try {
      const response = await axiosObj.patch('trade/manualClosePosition', closePosition);
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: 'Failed to Close Position' });
    }
  }
);

export const assignBarNumber = createAsyncThunk(
  "/trade/assignBarNumber",
  async (data: BarNumber, { rejectWithValue }) => {
    try {
      const response = await axiosObj.patch('trade/barNumber', data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: 'Failed to Assign Bar Number' });
    }
  }
);

const mapToHistory = (data: any[]) => {
  return data.map((item) => {
    const openingTime = new Date(item.openingTime);
    const closingTime = new Date(item.closingTime);
    const timeDiffMs = closingTime.getTime() - openingTime.getTime();

    return {
      _id: item._id,
      account: item.account,
      openingTime: toUAETime(item.openingTime),
      script: item.script,
      id: item.orderId,
      tradeType: item.tradeType,
      tradeMoney: item.tradeMoney ? item.tradeMoney.toFixed(2) : '-',
      tradeMetal: item.tradeMetal ? item.tradeMetal.toFixed(2) : '-',
      openPrice: item.openPrice ? item.openPrice.toFixed(2) : '-',
      closingTime: toUAETime(item.closingTime),
      takeProfit: item.takeProfit ? item.takeProfit : '-',
      timeDiff: formatTimeDiff(timeDiffMs),
      commission: item.commission,
      closePrice: item.closePrice ? item.closePrice.toFixed(2) : '-',
      pnl: item.pnl ? item.pnl.toFixed(2) : '-',
      comment: item.comment,
      tradeStatus: item.tradeStatus,
      dealId: item.dealId,
      orderId: item.orderId,
      userId: item.userId,
      tradeSymbol: item.tradeSymbol
    };
  });
};

const historySlice = createSlice({
  name: "history",
  initialState,
  reducers: {
    updateOpenTrade: (state, action) => {
      const positionIndex = state.openPositions.findIndex(item => item.dealId === action.payload.dealId);
      if (positionIndex !== -1) {
        const currentPosition = state.openPositions[positionIndex];
        const updatedUnfilledMetal = currentPosition.unfilledMetal - action.payload.amount;
        state.openPositions[positionIndex] = {
          ...state.openPositions[positionIndex],
          unfilledMetal: updatedUnfilledMetal
        }
      }
    },
    updateBarNumber: (state, action) => {
      const positionIndex = state.openPositions.findIndex(item => item._id === action.payload.tradeId);
      if (positionIndex !== -1) {
        state.openPositions[positionIndex] = {
          ...state.openPositions[positionIndex],
          barNumber: action.payload.barNumber
        }
      }
    }
  },
  extraReducers(builder) {
    builder
      .addCase(getHistory.pending, (state) => {
        state.listLoading.history = true;
      })
      .addCase(getHistory.fulfilled, (state, action) => {
        state.listLoading.history = false;
        state.history = mapToHistory(action.payload.payload.allTrades);
        state.listPagination.history = readPagination(action.payload.payload);
      })
      .addCase(getHistory.rejected, (state) => {
        state.listLoading.history = false;
        state.history = [];
      })
      .addCase(getOpenedPositions.pending, (state) => {
        state.listLoading.open = true;
      })
      .addCase(getOpenedPositions.fulfilled, (state, action) => {
        state.listLoading.open = false;
        state.openPositions = action.payload.payload.allBuyTrades;
        state.metalSummary = action.payload.payload.metalSummary;
        state.listPagination.open = readPagination(action.payload.payload);
      })
      .addCase(getOpenedPositions.rejected, (state) => {
        state.listLoading.open = false;
        state.openPositions = [];
        state.metalSummary = [];
      })
      .addCase(getClosedPositions.pending, (state) => {
        state.listLoading.closed = true;
      })
      .addCase(getClosedPositions.fulfilled, (state, action) => {
        state.listLoading.closed = false;
        state.closedPositions = action.payload.payload.sellTrades;
        state.metalSummary = action.payload.payload.metalSummary;
        state.listPagination.closed = readPagination(action.payload.payload);
      })
      .addCase(getClosedPositions.rejected, (state) => {
        state.listLoading.closed = false;
        state.closedPositions = [];
        state.metalSummary = [];
      })
      .addCase(getPendingBuyPositions.pending, (state) => {
        state.listLoading.pendingBuy = true;
      })
      .addCase(getPendingBuyPositions.fulfilled, (state, action) => {
        state.listLoading.pendingBuy = false;
        state.pendingBuyPositions = action.payload.payload.pendingBuyTrades;
        state.metalSummary = action.payload.payload.metalSummary;
        state.listPagination.pendingBuy = readPagination(action.payload.payload);
      })
      .addCase(getPendingBuyPositions.rejected, (state) => {
        state.listLoading.pendingBuy = false;
        state.pendingBuyPositions = [];
        state.metalSummary = [];
      })
      .addCase(getPendingSellPositions.pending, (state) => {
        state.listLoading.pendingSell = true;
      })
      .addCase(getPendingSellPositions.fulfilled, (state, action) => {
        state.listLoading.pendingSell = false;
        state.pendingSellPositions = action.payload.payload.pendingSellTrades;
        state.metalSummary = action.payload.payload.metalSummary;
        state.listPagination.pendingSell = readPagination(action.payload.payload);
      })
      .addCase(getPendingSellPositions.rejected, (state) => {
        state.listLoading.pendingSell = false;
        state.pendingSellPositions = [];
        state.metalSummary = [];
      })
      .addCase(getPositionStats.pending, (state) => {
        state.positionStatsLoading = true;
        state.positionStatsError = null;
      })
      .addCase(getPositionStats.fulfilled, (state, action) => {
        state.positionStatsLoading = false;
        state.positionStats = action.payload;
      })
      .addCase(getPositionStats.rejected, (state, action) => {
        state.positionStatsLoading = false;
        state.positionStatsError = action.payload ?? 'Failed to Fetch Position Stats';
      })
      .addCase(manualClosePosition.pending, (state) => {
        state.isMutating = true;
      })
      .addCase(manualClosePosition.fulfilled, (state) => {
        state.isMutating = false;
      })
      .addCase(manualClosePosition.rejected, (state) => {
        state.isMutating = false;
      })
      .addCase(assignBarNumber.pending, (state) => {
        state.isMutating = true;
      })
      .addCase(assignBarNumber.fulfilled, (state) => {
        state.isMutating = false;
      })
      .addCase(assignBarNumber.rejected, (state) => {
        state.isMutating = false;
      });
  }
})

export const { updateOpenTrade, updateBarNumber } = historySlice.actions;

export default historySlice.reducer;